import { Box, Button, Input, List, ListItem } from "@chakra-ui/react";
import { useI18n } from "next-localization";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { useDetectClickOutside } from "react-detect-click-outside";
import { pxToRem } from "themes/utils/mapPxToRem";
interface SearchTypeaheadProps {
    typeAheadUrl: string;
}

interface SearchResult {
    Term: string;
    AverageNumberOfResults: number;
}

const SearchTypeahead: React.FC<SearchTypeaheadProps> = (props: SearchTypeaheadProps) => {
    const [suggestions, setSuggestions] = useState<SearchResult[]>([]);
    const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");
    const inputRef = useRef<HTMLInputElement>(null);
    const { asPath, query, push } = useRouter();
    const pageUrl = asPath.split("?")[0];
    const { t } = useI18n();

    const emptyDropdown = () => {
        setSuggestions([]);
    };
    const ref = useDetectClickOutside({ onTriggered: () => emptyDropdown() });

    useEffect(() => {
        if (query.q === "") {
            setCurrentSearchTerm("");
            if (inputRef.current) {
                inputRef.current.value = "";
            }
        }
    }, [query]);

    const createTypeAheadListitem = (term: string, searchCount: number) => {
        return (
            <ListItem
                borderBottom="1px solid black"
                _last={{
                    borderBottom: "none",
                    borderBottomLeftRadius: pxToRem("3px"),
                    borderBottomRightRadius: pxToRem("3px"),
                }}
                _first={{
                    borderTopLeftRadius: pxToRem("3px"),
                    borderTopRightRadius: pxToRem("3px"),
                }}
                key={term}
            >
                <Button
                    justifyContent={"space-between"}
                    bg="transparent"
                    textTransform={"none"}
                    textAlign={"left"}
                    color="black"
                    borderRadius={"base"}
                    width="100%"
                    px="sp16"
                    gap="sp8"
                    onClick={() => {
                        setCurrentSearchTerm(term);
                        push({ pathname: pageUrl, query: { q: term } });
                    }}
                >
                    <Box as="span" height={"1.1em"} noOfLines={1}>
                        {term}
                    </Box>
                    <Box as="span">({searchCount})</Box>
                </Button>
            </ListItem>
        );
    };

    useEffect(() => {
        if (inputRef.current && currentSearchTerm !== "") {
            inputRef.current.value = currentSearchTerm;
            setSuggestions([]);
        }
    }, [currentSearchTerm]);

    const requestSuggestions = (suggestion: string) => {
        if (suggestion) {
            fetch(`${props.typeAheadUrl}${encodeURIComponent(suggestion)}`, {
                method: "get",
            })
                .then((response) => {
                    if (response.status !== 200) {
                        console.log(
                            "Looks like there was a problem. Status Code: " + response.status
                        );
                        return;
                    }

                    response
                        .json()
                        .then((data: SearchResult[]) => {
                            if (data) {
                                setSuggestions(data);
                            } else {
                                emptyDropdown();
                            }
                        })
                        .catch((err) => {
                            console.log("Error parsing JSON:", err);
                        });
                })
                .catch((err) => {
                    console.log("Fetch Error :-S", err);
                });
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value.length > 1) {
            requestSuggestions(value);
        }
    };

    return (
        <Box position={"relative"} ref={ref}>
            <Input
                ref={inputRef}
                px="sp16"
                py="sp32"
                autoComplete="off"
                type="text"
                name="q"
                aria-label={t("searchPlaceholder")}
                placeholder={t("searchPlaceholder")}
                borderRadius="base"
                defaultValue={query.q as string}
                onChange={handleInputChange}
                pr={"sp120"}
                _focus={{
                    outline: "1px solid black",
                }}
            />
            <List
                position={"absolute"}
                mt="sp12"
                left="0"
                bg="white"
                zIndex="1"
                border="1px solid black"
                borderRadius={"base"}
                width={"100%"}
                display={suggestions.length.toString() === "0" ? "none" : "block"}
            >
                {suggestions.map((result) =>
                    createTypeAheadListitem(result.Term, result.AverageNumberOfResults)
                )}
            </List>
        </Box>
    );
};

export default SearchTypeahead;
